import { EventHandleSpec } from '../EventHandleSpec';
import { Gesture, GestureEventInfo } from './Gesture';
import { GestureType, PseudoEventName } from '../../gen-src/preslayer/Enums';
import { NormalizedEvent } from '../event/NormalizedEvent';

/**
 * This class implements the pinch gesture
 */
class Pinch extends Gesture {
  private pinching: boolean = false;
  private touch1: Touch;
  private touch2: Touch;
  private startParams: GestureEventInfo;
  private lastParams: GestureEventInfo;

  /**
   * Initializes a new instance of the Pinch class
   * @param {EventHandleSpec} config - GestureEventHandleSpec object
   */
  public constructor(config: EventHandleSpec) {
    super(config, GestureType.Pinch, /*hasSlop:*/true, /*numTouches:*/2);
  }

  public Start(e: NormalizedEvent): void {
    if (e.touches.length < this.numTouches) {
      return;
    }

    if (this.pinching) {
      return;
    }

    this.touch1 = e.touches[0];
    this.touch2 = e.touches[1];
    this.startParams = this.GetStartParams();
    this.lastParams = this.startParams;
    let param: GestureEventInfo = this.GetParams();
    this.Fire(PseudoEventName.PinchStart, e, param);
    this.pinching = true;
  }

  public Move(e: NormalizedEvent): void {
    if (!this.pinching) {
      return;
    }
    if (e.touches.length < 2) { // BUGZID: 63702 Pinching ended, but this recognizer was not informed
      this.End(e); // Let's end the pinch
      return;
    }

    this.touch1 = e.touches[0];
    this.touch2 = e.touches[1];
    this.lastParams = this.GetParams();
    this.Fire(PseudoEventName.PinchMove, e, this.lastParams);
  }

  public End(e: NormalizedEvent): void {
    if (e.touches.length > this.numTouches) {
      return;
    }

    if (this.pinching) {
      this.pinching = false;
      let param: GestureEventInfo = this.GetParams();
      this.Fire(PseudoEventName.PinchEnd, e, param);
    }
  }

  public Cancel(): void {
    // Do nothing
  }

  private GetStartParams(): GestureEventInfo {
    let t1: Touch = this.touch1;
    let t2: Touch = this.touch2;
    let distX: number = t2.pageX - t1.pageX;
    let distY: number = t2.pageY - t1.pageY;
    let distance: number = Math.sqrt((distX * distX) + (distY * distY));
    let centerX: number = (distX / 2) + t1.pageX;
    let centerY: number = (distY / 2) + t1.pageY;
    let params = new GestureEventInfo();
    params.pageX = centerX;
    params.pageY = centerY;
    params.distance = distance;
    return params;
  }

  private GetParams(): GestureEventInfo {
    let t1: Touch = this.touch1;
    let t2: Touch = this.touch2;
    let distX: number = t2.pageX - t1.pageX;
    let distY: number = t2.pageY - t1.pageY;
    let distance: number = Math.sqrt((distX * distX) + (distY * distY));
    let centerX: number = (distX / 2) + t1.pageX;
    let centerY: number = (distY / 2) + t1.pageY;
    let params = new GestureEventInfo();
    params.pageX = centerX;
    params.pageY = centerY;
    params.scale = distance / this.startParams.distance;
    params.deltaX = centerX - this.startParams.pageX;
    params.deltaY = centerY - this.startParams.pageY;
    params.startX = this.startParams.pageX;
    params.startY = this.startParams.pageY;
    params.deltaXSinceLast = centerX - this.lastParams.pageX;
    params.deltaYSinceLast = centerY - this.lastParams.pageY;
    return params;
  }
}

export { Pinch };
